import { z } from "zod";
import { Method, SourceRef, Timestamp } from "./primitives.js";
/**
 * Discrete named findings. Deliberately not a composite score: a blended number invents
 * precision the evidence does not support.
 */
export const FlagKind = z.enum([
    "debt_pushed_down",
    "index_exclusion_likely",
    "parent_retains_stake",
    "float_capped",
    "insider_buying",
    "no_analyst_coverage",
    "management_incentive_reset",
    "segment_restated",
    "tsa_dependency",
    "terms_changed",
    "rmt_structure",
    "stage_stalled",
]);
/** How much attention a flag asks for, least to most. */
export const Severity = z.enum(["info", "watch", "critical"]);
/** Each severity's rank for sorting; a higher number is more severe. */
export const SEVERITY_ORDER = {
    info: 0,
    watch: 1,
    critical: 2,
};
/** A named finding. `evidence` is non-empty: a flag with no evidence is a bug. */
export const Flag = z.object({
    id: z.string().uuid(),
    situationId: z.string().uuid(),
    kind: FlagKind,
    severity: Severity,
    summary: z.string().min(1),
    evidence: z.array(SourceRef).min(1, "A flag with no evidence is a bug, not a finding"),
    method: Method,
    detectedAt: Timestamp,
    dismissedAt: Timestamp.nullable(),
    dismissedReason: z.string().nullable(),
});
/** Human-facing copy per flag kind. Kept beside the enum so the two cannot drift. */
export const FLAG_LABELS = {
    debt_pushed_down: {
        title: "Debt pushed down",
        blurb: "SpinCo carries debt disproportionate to the earnings travelling with it.",
    },
    index_exclusion_likely: {
        title: "Index exclusion likely",
        blurb: "SpinCo's expected size falls below the parent's benchmark, forcing index selling.",
    },
    parent_retains_stake: {
        title: "Parent retains a stake",
        blurb: "The parent keeps shares it may later dispose of, an overhang on the new float.",
    },
    float_capped: {
        title: "Float capped",
        blurb: "Initial tradeable float is small enough that mechanical selling moves the price.",
    },
    insider_buying: {
        title: "Insider buying",
        blurb: "Open-market Form 4 purchases after distribution.",
    },
    no_analyst_coverage: {
        title: "No analyst coverage",
        blurb: "No sell-side coverage established at separation.",
    },
    management_incentive_reset: {
        title: "Incentives reset",
        blurb: "New equity grants struck against a post-separation reference price.",
    },
    segment_restated: {
        title: "Segment restated",
        blurb: "The parent restated this segment's financials during registration.",
    },
    tsa_dependency: {
        title: "TSA dependency",
        blurb: "SpinCo depends on the parent for services under a transition agreement.",
    },
    terms_changed: {
        title: "Terms changed",
        blurb: "Ratio, dates or capital structure moved between amendments.",
    },
    rmt_structure: {
        title: "Reverse Morris Trust",
        blurb: "Separation combined with a merger to achieve tax-free treatment.",
    },
    stage_stalled: {
        title: "Stalled",
        blurb: "No advancing filing for an unusually long period at this stage.",
    },
};
//# sourceMappingURL=flag.js.map